// Elevation Tinting System - MUI Compatible Version
// This uses MUI's getColorSchemeSelector to apply tinting styles for dark mode only
// Light mode uses standard MUI shadows (configured in muiTheme.ts)
//
// WARNING: This is a hack using backgroundImage instead of backgroundColor
//
// Why this horrible hack exists:
// 1. MUI's extendTheme evaluates styleOverrides ONCE at build time, not at runtime
// 2. Using theme.palette.mode or theme.palette.background.paper in styleOverrides
//    gets the value at theme creation time and never updates when mode switches
// 3. MUI sets backgroundColor using CSS variables with high specificity that we can't override
// 4. Attempts to inject custom CSS variables failed because MUI doesn't process them properly
// 5. The ONLY reliable way to have mode-specific styles is using theme.getColorSchemeSelector()
//
// So we use backgroundImage with a semi-transparent gradient as a hack to layer
// tinting on top of whatever background color MUI sets. This is terrible but it works.
//
// TODO(1): Restructure this to use a proper theming system that allows dynamic
// background colors based on elevation. Options:
// - Fork MUI's theme system to support elevation-based colors natively
// - Use CSS-in-JS with runtime evaluation instead of static theme overrides
// - Switch to a different component library that supports MD3 elevation natively
// - Build custom Card/Paper components that handle elevation properly

// Tint opacities by elevation level
const TINT_OPACITIES = {
  light: [0, 0, 0.05, 0.11, 0.14, 0.17],
  dark: [0, 0, 0.08, 0.12, 0.15, 0.18],
} as const;

// Create elevation styles that work with MUI's color scheme system
export function createElevationStyles(theme: any, elevation: number) {
  const clampedElevation = Math.max(0, Math.min(5, Math.round(elevation)));

  const darkOpacity = TINT_OPACITIES.dark[clampedElevation];

  // Return styles for dark mode only (light mode uses standard shadows)
  return {
    // Dark mode styles - use MUI's selector
    [theme.getColorSchemeSelector('dark')]: {
      ...(darkOpacity > 0 && {
        backgroundImage: `linear-gradient(rgba(255, 255, 255, ${darkOpacity}), rgba(255, 255, 255, ${darkOpacity}))`,
      }),
    },
  };
}

// Create hover elevation styles
export function createHoverElevationStyles(theme: any, elevation: number) {
  const clampedElevation = Math.max(0, Math.min(5, Math.round(elevation)));
  const hoverElevation = Math.min(5, clampedElevation + 1);

  const darkHoverOpacity = TINT_OPACITIES.dark[hoverElevation];

  return {
    '&:hover': {
      // Dark mode hover styles only (light mode uses standard shadows)
      [theme.getColorSchemeSelector('dark')]: {
        ...(darkHoverOpacity > 0 && {
          backgroundImage: `linear-gradient(rgba(255, 255, 255, ${darkHoverOpacity}), rgba(255, 255, 255, ${darkHoverOpacity}))`,
        }),
      },
    },
  };
}
